import Box from "ds/components/Box";
import Card from "ds/components/Card";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import TooltipInfo from "ds/components/TooltipInfo";
import { IconComponent } from "types/Icon";
import { IconEmphasizedColor } from "ds/components/IconEmphasized/types";

import LaunchPadTrackMissionCompletionCardsSkeleton from "./MissionCompletionCardsSkeleton";

const MAX_COLUMNS = 4;

const getColumnCount = (itemCount: number) => {
  if (itemCount <= MAX_COLUMNS) return itemCount;
  const rows = Math.ceil(itemCount / MAX_COLUMNS);
  return Math.ceil(itemCount / rows);
};

export type LaunchPadTrackMission = {
  id: string;
  label: string;
  icon: IconComponent;
  iconColor: IconEmphasizedColor;
  completedCount: number;
  startedCount: number;
};

type LaunchPadTrackMissionCompletionCardsProps = {
  missions: LaunchPadTrackMission[];
  totalUsers: number;
  loading: boolean;
};

const LaunchPadTrackMissionCompletionCards = ({
  missions,
  totalUsers,
  loading,
}: LaunchPadTrackMissionCompletionCardsProps) => {
  if (loading) {
    return <LaunchPadTrackMissionCompletionCardsSkeleton />;
  }

  if (!missions.length) {
    return null;
  }

  const columnCount = getColumnCount(missions.length);

  return (
    <Box grid gridTemplate={`repeat(${columnCount}, 1fr)`} gap="large">
      {missions.map((mission) => {
        const completionPercentage =
          totalUsers > 0 ? Math.round((mission.completedCount / totalUsers) * 100) : 0;

        return (
          <Card key={mission.id} fullWidth zeroMinWidth>
            <Box direction="column" gap="medium" zeroMinWidth>
              <Box direction="row" align="center" gap="small" zeroMinWidth>
                <TextEllipsis tooltip={mission.label} tooltipWidthMode="maxWidthSm">
                  {(ellipsisProps) => (
                    <Typography variant="p-t7" tag="span" {...ellipsisProps}>
                      {mission.label}
                    </Typography>
                  )}
                </TextEllipsis>
                <Box shrink="0">
                  <TooltipInfo>
                    Completed: {mission.completedCount} users
                    <br />
                    Started: {mission.startedCount} users
                  </TooltipInfo>
                </Box>
              </Box>
              <TextEllipsis tooltip={`${completionPercentage}%`} tooltipWidthMode="maxWidthSm">
                {(ellipsisProps) => (
                  <Typography variant="p-t2" tag="span" color="primary" {...ellipsisProps}>
                    {completionPercentage}%
                  </Typography>
                )}
              </TextEllipsis>
              <TextEllipsis tooltip="Completed" tooltipWidthMode="maxWidthSm">
                {(ellipsisProps) => (
                  <Typography variant="p-body3" tag="span" color="secondary" {...ellipsisProps}>
                    Completed
                  </Typography>
                )}
              </TextEllipsis>
            </Box>
          </Card>
        );
      })}
    </Box>
  );
};

export default LaunchPadTrackMissionCompletionCards;
